/**
 * Main de pointage DESSINÉE PAR NOUS, pas par le système d'exploitation.
 *
 * 🔴 Le fait qui justifie ce module (signalé le 2026-08-15) : « sur certains
 * ordi la main est invisible, parfois pas de contour noir, que du blanc ».
 * Ce n'est pas un aléa d'affichage. `cursor: pointer` ne décrit qu'une
 * INTENTION — le glyphe, lui, est dessiné par l'OS, et les OS ne le dessinent
 * pas pareil : la main de macOS a un contour NOIR franc, celle de Windows 11
 * est blanche à contour GRIS CLAIR. Or la convention de fond de ce produit est
 * `bg-white` sur toute surface interactive (cf. « UI background convention »
 * dans CLAUDE.md) : sur Windows, la main disparaît donc exactement là où l'on
 * clique. S'y ajoutent les thèmes de curseurs et les réglages d'accessibilité
 * (macOS expose « couleur de contour » ET « couleur de remplissage »), si bien
 * que même « macOS a un contour noir » n'est pas une garantie.
 *
 * La seule façon de rendre le contour certain est d'arrêter de le déléguer :
 * le navigateur rastérise l'image qu'on lui donne, et le contour est dans NOS
 * pixels.
 *
 * 🔴 **LA FORME EST RELEVÉE SUR LE VRAI CURSEUR, elle n'est pas inventée**
 * (2026-08-15). La première version dessinait quatre rectangles arrondis et une
 * ligne épaissie en guise de pouce : le résultat se lisait comme une moufle,
 * sans doigts ni plis. Le tracé actuel est calqué au pixel près sur
 * `NSCursor.pointingHand` de macOS — bitmap 32×32, point actif (13, 8) — dont
 * il reprend les six traits caractéristiques : index effilé qui s'évase vers la
 * base, trois doigts repliés dont les sommets s'étagent de gauche à droite,
 * bande de phalanges laissée BLANCHE, trois plis dans la paume, encoche entre
 * le talon et la base de l'auriculaire, pouce et bas-gauche de la paume formant
 * UNE seule arête (la diagonale du poignet).
 *
 * 🔴 **SOURCE UNIQUE — ce fichier n'a AUCUN import, et ce n'est pas un hasard.**
 * Il est chargé par `tailwind.config.ts` (donc par jiti, hors du résolveur de
 * chemins de TypeScript) autant que par l'application. Y ajouter un `import`,
 * même de type, peut casser le build CSS. Ses deux consommateurs :
 *   · `tailwind.config.ts` — redéfinit la VALEUR de l'utilitaire
 *     `cursor-pointer`, donc les ~70 appels existants en héritent sans être
 *     touchés, et la cascade reste identique à avant (un
 *     `disabled:cursor-not-allowed` continue de l'emporter, ce qu'une
 *     surcharge à coups de sélecteurs aurait cassé) ;
 *   · `website/editor/canvas/styles.ts` — l'iframe du canvas a son propre
 *     document, sans Tailwind : elle pose la valeur à la main.
 */

/**
 * 🔴 32, et pas 28 comme jusqu'au 2026-08-15 — pour DEUX raisons distinctes.
 *
 * 1. C'est le maximum sûr. Au-delà, les navigateurs ignorent purement et
 *    simplement un curseur personnalisé et retombent sur le mot-clé de repli,
 *    **sans le moindre signal** : on ne verrait pas la panne, on verrait la
 *    main du système revenir — c'est-à-dire le défaut que ce module corrige.
 * 2. C'est la seule taille où le tracé tombe PILE sur la grille de pixels
 *    (1 unité du `viewBox` = 1 pixel rendu), ce dont dépend la netteté : à 28,
 *    chaque unité vaut 0,875 pixel, un pli de 1 unité s'étale sur deux colonnes
 *    et sort en GRIS — soit, à nouveau, le défaut d'origine, juste plus discret.
 *    Mesuré par rastérisation puis agrandissement sans lissage.
 */
const SIZE = 32;

/**
 * Le bout de l'index. Reprend exactement le point actif d'Apple.
 *
 * ⚠️ Il ne se déduit PAS du dessin : c'est l'endroit qui clique. Le déplacer
 * décale silencieusement toutes les cibles de l'application d'autant de pixels.
 */
const HOTSPOT_X = 13;
const HOTSPOT_Y = 8;

/**
 * Épaisseur du contour, en unités de la grille de 32.
 *
 * Apple tourne autour de 0,9 ; on assume un peu plus gras. Le contour EST la
 * fonction de ce module : c'est lui qui doit survivre à un fond gris moyen et à
 * l'antialiasing d'un écran non-Retina, où 1 pixel de noir sur blanc se rend en
 * gris à 50 %. La contrepartie est un dessin légèrement plus dense que
 * l'original — arbitrage assumé en faveur de la lisibilité.
 */
const OUTLINE = 1.25;

/** Noir du contour et blanc du remplissage. Le contraste prime sur la palette. */
const INK = "#111827";
const PAPER = "#ffffff";

/**
 * Les cinq CORPS BLANCS. Le contour n'est jamais tracé comme tel : il naît de
 * la passe sombre qui déborde de ces corps (cf. {@link HAND_CURSOR_SVG}). Toute
 * cote ci-dessous est donc relevée sur le BLANC du bitmap d'Apple.
 *
 * ⚠️ L'ordre est l'ordre de peinture : la paume vient en dernier et recouvre le
 * bas des quatre doigts, ce qui produit la bande de phalanges blanche. Inverser
 * ferait remonter les séparations sombres jusque dans la paume.
 */
const BODIES =
  // Index — sommet à y=9,5, large de 1 unité en haut, s'évase vers la base.
  // 🔴 Ses deux bords verticaux sont posés sur des ENTIERS (12 et 13) : avec un
  // contour de 1,25 la colonne de blanc tombe pile entre deux colonnes sombres,
  // et la pointe reste franche au lieu de baver sur trois pixels gris.
  `<path d='M12 9.5 A0.5 0.5 0 0 1 13 9.5 L13.85 17.9 L12 17.9 Z'/>` +
  // Majeur, annulaire, auriculaire — sommets étagés (13,4 · 13,9 · 14,85).
  // L'espace laissé entre deux doigts est plus étroit que 2×OUTLINE : la passe
  // sombre le comble donc entièrement, ce qui dessine leur séparation sans
  // qu'aucun trait n'ait à être tracé.
  `<path d='M15.3 13.4 A0.65 0.65 0 0 1 16.6 13.4 L16.8 17.9 L15.25 17.9 Z'/>` +
  `<path d='M18.05 13.9 A0.65 0.65 0 0 1 19.35 13.9 L19.5 17.9 L18 17.9 Z'/>` +
  `<path d='M20.7 14.85 A0.6 0.6 0 0 1 21.9 14.85 L22 17.9 L20.7 17.9 Z'/>` +
  // Paume + pouce, d'un seul tenant. Le V (19.1 → 17.85 → 16.75) est l'encoche
  // du talon ; il est taillé PROFOND dans le blanc parce que la passe sombre,
  // en dilatant, referme les angles rentrants — un V peu marqué ici ne
  // laisserait aucune encoche visible à l'arrivée.
  `<path d='M11.3 15.05 L21.9 15.55` +
  ` C22.15 17.6 22 19.6 20.85 21.2 C20.45 21.9 20.15 22.35 19.7 22.65` +
  ` L19.1 22.95 L17.85 20.1 L16.75 22.9 L14.9 22.95` +
  ` C14 22.95 13.55 22.35 13.95 21.75 L9.75 16.75` +
  ` C9 15.9 8.4 15.55 9.15 15.1 C9.9 14.7 10.75 14.85 11.3 15.05 Z'/>`;

/**
 * Les trois plis de la paume.
 *
 * 🔴 Chaque pli est centré sur un DEMI-entier et large d'exactement 1 unité :
 * il remplit donc une colonne de pixels entière, et une seule. Décalé de 0,25,
 * le même trait s'étale sur deux colonnes à 62 % / 38 % et les trois plis
 * deviennent une tache grise. C'est le détail le plus fragile du dessin.
 */
const CREASES =
  `<path d='M15.5 17 L15.5 20.5'/>` +
  `<path d='M17.5 17 L17.5 20.5'/>` +
  `<path d='M19.5 17 L19.5 20.5'/>`;

/**
 * Dessiné en TROIS PASSES — silhouette sombre épaissie, corps blancs, puis
 * plis — plutôt qu'en forme unique contournée : l'union des cinq corps se fait
 * toute seule et aucune couture interne n'apparaît, là où un tracé unique
 * demanderait un chemin de contour exact.
 *
 * ⚠️ L'ORDRE des passes est porteur, dans les deux sens :
 *  · passes 1 et 2 inversées, le blanc passerait dessous et l'on retomberait
 *    sur une main sans contour, c'est-à-dire sur le défaut même que ce module
 *    corrige ;
 *  · les plis AVANT le blanc seraient recouverts par lui et disparaîtraient.
 * Verrouillé par test dans les deux sens.
 */
export const HAND_CURSOR_SVG =
  `<svg xmlns='http://www.w3.org/2000/svg' width='${SIZE}' height='${SIZE}' viewBox='0 0 32 32'>` +
  // 1 · la silhouette : le contour EST ce débordement, d'où le ×2 (un trait
  // centré sur le bord du corps ne déborde que de la moitié de sa largeur).
  `<g fill='${INK}' stroke='${INK}' stroke-width='${OUTLINE * 2}' stroke-linejoin='round'>${BODIES}</g>` +
  // 2 · les mêmes corps en blanc, qui ne laissent dépasser que le contour. Le
  // filet de 0,15 comble la couture d'antialiasing entre remplissage et bord.
  `<g fill='${PAPER}' stroke='${PAPER}' stroke-width='0.15' stroke-linejoin='round'>${BODIES}</g>` +
  // 3 · les plis, PAR-DESSUS le blanc. `butt` et pas `round` : un bout arrondi
  // mangerait un demi-pixel à chaque extrémité et regriserait le trait.
  `<g stroke='${INK}' stroke-width='1' stroke-linecap='butt' fill='none'>${CREASES}</g>` +
  `</svg>`;

/**
 * Valeur complète de la propriété `cursor`.
 *
 * ⚠️ Le mot-clé de repli (ici `pointer`) est **exigé par la spec** après une
 * `url()` — sans lui la déclaration entière est invalide, et l'on ne
 * dégraderait pas vers la main du système : on perdrait le curseur.
 *
 * ⚠️ Contrepartie assumée : taille FIXE. Quelqu'un qui a agrandi ses curseurs
 * dans les réglages d'accessibilité de son système la verra plus petite que
 * les siens — CSS ne donne aucun moyen de lire ce réglage. On échange une
 * taille adaptative contre un contour garanti.
 */
export const HAND_CURSOR_CSS =
  `url("data:image/svg+xml,${encodeURIComponent(HAND_CURSOR_SVG)}") ${HOTSPOT_X} ${HOTSPOT_Y}, pointer`;
